// CACHE BUST v2.0 - Contemporary design with glass morphism
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PDFLeadMagnet from '../components/PDFLeadMagnet.jsx';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import ProgramModules from '../components/ProgramModules.jsx';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver.jsx';
function GroupCoachingService() {
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [openModule, setOpenModule] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [heroRef, heroVisible] = useIntersectionObserver({
    threshold: 0.2
  });

  // Handle scroll for navigation
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO Optimization
  useEffect(() => {
    if (typeof document === 'undefined') return;
    // Set page title
    document.title = 'Group Coaching Program | 40-Week Coaching for People Pleasers | Online';

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Join our trauma-informed group coaching program to overcome people-pleasing. Early bird special $1,977 (save $1,000!). 40 modules teaching nervous system regulation, boundaries, and authentic connection. Virtual sessions worldwide.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Join our trauma-informed group coaching program to overcome people-pleasing. Early bird special $1,977 (save $1,000!). 40 modules teaching nervous system regulation, boundaries, and authentic connection. Virtual sessions worldwide.';
      document.head.appendChild(meta);
    }

    // Set meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'group coaching, people pleasing therapy, nervous system regulation, trauma-informed coaching, boundary setting, online group therapy, virtual coaching, compassionate connect method');
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      metaKeywords.content = 'group coaching, people pleasing therapy, nervous system regulation, trauma-informed coaching, boundary setting, online group therapy, virtual coaching, compassionate connect method';
      document.head.appendChild(metaKeywords);
    }

    // Add Open Graph tags
    const setOGTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };
    setOGTag('og:title', 'Group Coaching Program | Early Bird $1,977 | Coaching for People Pleasers');
    setOGTag('og:description', 'Join our 40-week trauma-informed group coaching program. Early bird special $1,977 (save $1,000). Learn nervous system regulation, boundaries, and authentic connection in a safe, virtual community.');
    setOGTag('og:type', 'website');

    // Cleanup function
    return () => {
      if (typeof document !== 'undefined') {
        document.title = 'Yasmin Kohli';
      }
    };
  }, []);
  const toggleModule = index => {
    setOpenModule(openModule === index ? null : index);
  };

  // Helper function to convert to title case
  const toTitleCase = str => {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };
  const modules = [{
    name: "Prepare and Plan: Intention, Attention, Energy, Power",
    description: "Learn what to expect from this journey and what is expected from you in terms of participation and confidentiality. Set clear intentions, understand where your energy and attention go, and begin to reclaim your power. Together, we create a safe space for all members to explore, share, and grow."
  }, {
    name: "People-pleasing Deep Dive",
    description: "Understand what people-pleasing truly is and why we do it. Explore the deeper patterns of overextending yourself, people-pleasing triggers, and practice saying, 'I need to think about it, let me get back to you' and 'I'll check my calendar and let you know.'"
  }, {
    name: "Grounded Body: My Window of Tolerance",
    description: "Discover what a grounded body is, how it feels in your system, and why grounding is essential to recover from people-pleasing patterns. Understand your window of tolerance, its edges, and how to expand it with self-compassion."
  }, {
    name: "Stress in Our Bodies and Stress Responses",
    description: "Explore the four stress responses: Fight, Flight, Freeze, and Fawn. Learn how these responses show up in your body and relationships, and begin to recognize your patterns."
  }, {
    name: "Listening to My Body",
    description: "Tune into sensations, feelings, and stuck trauma held in your body. Begin to hear what your body has been trying to tell you all along. Connect with your body's wisdom and learn to trust its messages."
  }, {
    name: "Feelings and Needs",
    description: "Explore the connection between your feelings and underlying needs. Learn to identify and express your needs while validating your emotional experience. Practice staying tapped into yourself in relationships."
  }, {
    name: "Root Cause: Complex Trauma",
    description: "Understand how complex trauma shapes people-pleasing patterns and recognize the deep roots of these protective behaviors. Explore how trauma lives in your body and affects your relationships."
  }, {
    name: "Root Cause: Gender Role Socialization",
    description: "Examine how gender role socialization conditioned you to prioritize others' needs and explore the cultural messages that shaped your patterns. Understand the systemic roots of people-pleasing."
  }, {
    name: "Inner Critic: Shame and Guilt",
    description: "Meet your inner critic with compassion and explore shame and guilt. Understand that there are no bad parts—people-pleasing serves a purpose. Address guilt around prioritizing yourself and recognize induced guilt from others."
  }, {
    name: "Nervous System Regulation",
    description: "Learn about the branches of your nervous system, why regulation matters, and discover the vagus nerve and vagus nerve stimulation techniques. Explore long-term regulation strategies for lasting nervous system health."
  }, {
    name: "Breaking the Patterns",
    description: "Practice uncoupling feelings and thoughts. Learn to separate automatic reactions from conscious responses and break old patterns. Challenge self-limiting beliefs and self-sabotage patterns that keep you stuck."
  }, {
    name: "My HopefulPath Plan",
    description: "Create your long-term plan for continued growth and healing. Celebrate how far you've come and envision your hopeful path forward with clarity, intention, and self-compassion."
  }];
  return <div className="min-h-screen bg-gradient-to-b from-white to-cream-50">
      {/* Navigation */}
      <Navigation scrolled={false} variant="transparent-to-solid" />
      
      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Course",
        "name": "Group Coaching Program",
        "description": "A trauma-informed, 40-week group coaching program for overcoming people-pleasing. Learn nervous system regulation, boundaries, and authentic connection in a safe, virtual community.",
        "provider": {
          "@type": "Person",
          "name": "Yasmin Kohli",
          "jobTitle": "Therapist & Coach"
        },
        "courseMode": "online",
        "duration": "P40W",
        "numberOfLessons": 40,
        "hasCourseInstance": {
          "@type": "CourseInstance",
          "courseMode": "online",
          "courseWorkload": "PT2H",
          "courseSchedule": {
            "@type": "Schedule",
            "repeatFrequency": "P1W",
            "byDay": "Thursday"
          }
        },
        "offers": [{
          "@type": "Offer",
          "name": "Early Bird Special - Pay in Full",
          "price": "2647",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "validFrom": "2024-10-29",
          "priceValidUntil": "2024-11-25"
        }, {
          "@type": "Offer",
          "name": "Early Bird Special - 3-Part Payment Plan",
          "price": "2724",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "validFrom": "2024-10-29",
          "priceValidUntil": "2024-11-25"
        }, {
          "@type": "Offer",
          "name": "Regular Price",
          "price": "2799",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "validFrom": "2024-11-26"
        }],
        "educationalLevel": "Beginner to Advanced",
        "teaches": ["Nervous system regulation", "Boundary setting", "People-pleasing recovery", "Trauma-informed practices", "Authentic self-expression"]
      })}
      </script>
      
      {/* Hero Banner Section - Full Width */}
      <section className="relative w-full">
        {/* Hero Image - Full Width */}
        <div className="relative w-full h-[480px] md:h-[560px] lg:h-[640px]">
          <img src="https://images.squarespace-cdn.com/content/v1/5e7be0f4a4a1a06a00b2ccbf/1760129962838-RBVQ8U0LV52G2ZV4K5G9/unsplash-image-roY7Lf1Zxl8.jpg" alt="Hands forming a supportive circle showing connection and community" className="w-full h-full object-cover" style={{
          objectPosition: 'center 20%'
        }} />
          
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
          
          {/* Text Content Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-4xl leading-10 leading-10 md:text-5xl md:leading-none md:leading-none lg:text-[3.78rem] lg:leading-none lg:leading-none [font-family:Cormorant_Garamond,serif] font-light text-white leading-tight [filter:drop-shadow(0_25px_25px_rgb(0,0,0,0.15))] mt-[215px] mb-3 ml-0 mr-0 pt-0 pb-0 pl-0 pr-0">
                The Compassionate Connect Method<br /><span className="font-bold text-[0.92em]">— CCM<sup className="text-[0.5em] ml-0.5">™</sup> —</span>
              </h1>
              <p className="text-xl md:text-2xl text-white font-light leading-relaxed mb-8 drop-shadow-lg max-w-4xl mx-auto">A Trauma-Informed Small Group Coaching Program<br />for Women who are Ready to Live, Love, and Lead Authentically.</p>
              
              {/* Limited Spots Badge */}
              <div className="inline-block bg-white text-sage-900 rounded-full shadow-[0_0_#0000,0_0_#0000,0_25px_50px_-12px_rgb(0,0,0,0.25)] mt-0 mb-3 ml-0 mr-0 pt-1 pb-1 pl-6 pr-6">
                <span className="text-lg font-bold">{"Path One Starts March, 2026"}</span>
              </div>
              
              {/* CTA Button */}
              <div>
                <Link to="/path-one-enrollment" className="inline-block bg-warmcream-600 text-white rounded-full text-xl leading-7 font-bold hover:bg-warmcream-700 transition-all ease-in-out duration duration-300 hover:shadow-[0_0_#0000,0_0_#0000,0_25px_50px_-12px_rgb(0,0,0,0.25)] hover:translate-x-0 hover:translate-y-0 hover:rotate-[0] hover:skew-x-[0] hover:skew-y-[0] hover:scale-105 translate-x-0 translate-y-0 rotate-[0] skew-x-[0] skew-y-[0] scale-100 mt-[0.75px] mb-[0.75px] ml-0 mr-0 pt-1 pb-1 pl-12 pr-12" data-tracking-id="link-972b631bb1ce06ce"><span>Sign me Up!</span></Link>
              </div>
            </div>
          </div>

          {/* Scroll Arrow */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-float">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-3 hover:scale-110 transition-transform duration-300 cursor-pointer">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </div>
      </section>
      
      {/* Quick Intro Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Special Holiday Launch Box */}
          <div className="bg-gradient-to-br from-warm-100 to-sage-100 border-2 border-warm-300 p-8 rounded-2xl mb-8 shadow-lg">
            <div className="text-center">
              <h3 className="font-serif font-light text-sage-800 mb-4 whitespace-nowrap" style={{
              fontSize: 'clamp(1.3rem, 2.5vw, 1.56rem)'
            }}>{"Path One Group starts March, 2026, Thursday 11am-12:30pm PST"}</h3>
              <p className="text-lg text-gray-700 mb-2"></p>
              <p className="text-xl font-semibold text-warm-700">We meet for 12 consecutive Thursdays.</p>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">The Group Coaching Program is a Three Path Journey</h2>
          
          <div className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
            <p>The Group Coaching Program is a trauma-informed, somatic, and experiential program designed to help you move from self-abandonment to self-trust.</p>
          </div>
          
          {/* Highlighted Availability Notice */}
          <div className="bg-gradient-to-br from-sage-50 to-warm-50 border-2 border-sage-300 p-8 md:p-10 rounded-2xl shadow-lg mb-8">
            <h3 className="text-2xl md:text-3xl font-serif font-light text-sage-900 mb-6"></h3>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">Each Path — One, Two, and Three — is a small, guided group offered worldwide through secure video conferencing. Each group meets for 12 weeks for 2-hour weekly group coaching sessions, with midweek support.<br /><br />Each group moves through its own phase of healing and growth, building connection, safety, and confidence along the way.</p>

          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            {/* Program Modules - Accordion Style */}
            <ProgramModules />
          </div>
        </div>
      </section>

      {/* Continuing Content After Image */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            {/* Paths Two and Three Box */}
            <div className="bg-gradient-to-br from-sage-100 to-warm-100 border-2 border-sage-300 p-8 rounded-2xl mb-12 shadow-lg">
              <div className="text-center">
                <h3 className="font-serif font-semibold text-sage-800 mb-4 whitespace-nowrap" style={{
                fontSize: 'clamp(1.3rem, 2.5vw, 1.56rem)'
              }}>Paths Two and Three groups start early 2026</h3>
                <p className="text-xl font-semibold text-sage-700 mb-6">Reserve your spot in an upcoming group below</p>
                
                {/* Waitlist Signup Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/waitlist-signup?path=2" className="px-8 py-4 bg-sage-600 text-white rounded-full font-semibold hover:bg-sage-700 transition-all duration-300 hover:shadow-xl text-center" data-tracking-id="link-810f38e1ff3cc8d5"><span>
                    Reserve Spot for Path Two
                  </span></Link>
                  <Link to="/waitlist-signup?path=3" className="px-8 py-4 bg-sage-600 text-white rounded-full font-semibold hover:bg-sage-700 transition-all duration-300 hover:shadow-xl text-center" data-tracking-id="link-3d74d0967eb7cb84"><span>
                    Reserve Spot for Path Three
                  </span></Link>
                </div>
              </div>
            </div>

            {/* Investment Section */}
            <div id="pricing" className="bg-gradient-to-br from-sage-50 to-cream-50 p-8 md:p-10 rounded-2xl mt-12 max-w-3xl mx-auto border-2 border-sage-200 shadow-lg mb-12">
              <div className="text-center mb-8">
                <div className="inline-block bg-sage-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  ✨ PATH 1 EARLY BIRD SPECIAL
                </div>
                <h3 className="text-3xl font-serif font-light text-sage-800 mb-2">Investment</h3>
                <p className="text-gray-600 mb-2">Each Path is 12 weeks of transformative group coaching</p>
                <p className="text-sm text-warm-700 font-semibold">Path One starts December 11, 2025</p>
              </div>

              {/* Path Pricing */}
              <div className="bg-white p-6 rounded-xl mb-6 border-2 border-warm-300">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Each Path Investment</p>
                  <div className="text-5xl md:text-6xl font-serif text-sage-800 mb-2">$1,977</div>
                  <p className="text-lg text-sage-600 font-medium">for one Path</p>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-warm-700 font-semibold">Early Bird Special for Path One</p>
                    <p className="text-sm text-gray-600">$100 discount available until December 8th.</p>
                  </div>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-4 mb-6">
                <h4 className="text-xl font-serif text-sage-800 text-center mb-4">Choose Your Payment Plan</h4>
                
                {/* Pay in Full */}
                <div className="bg-sage-800 text-white p-5 rounded-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 text-white px-3 py-1 text-xs leading-4 font-bold rounded-bl-lg bg-[#243719]"></div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-90 mb-1">Pay in Full</p>
                      <p className="text-3xl font-serif font-bold">$1,977</p>
                      <p className="text-sm opacity-90 mt-1"></p>
                    </div>
                    <div className="text-right">
                      <div className="bg-white text-sage-800 px-4 py-2 rounded-lg font-semibold text-sm">
                        One Payment
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3 Payment Plan */}
                <div className="bg-white p-5 rounded-xl border-2 border-warm-300">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">3 Monthly Payments</p>
                      <p className="text-3xl font-serif font-bold text-sage-800">$659</p>
                      <p className="text-sm text-gray-600 mt-1">per month (Total: $1,977)</p>
                    </div>
                    <div className="text-right">
                      <div className="bg-warm-500 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                        3 Payments
                      </div>
                    </div>
                  </div>
                </div>


              </div>

              {/* Limited Spots - Premium Experience Highlight */}
              <div className="mt-6 p-6 bg-gradient-to-br from-amber-50 to-warm-50 border-2 border-amber-300 rounded-xl shadow-md">
                <div className="text-center">
                  <span className="text-3xl font-bold text-amber-900 block mb-3">🌟 Only 10 Spots — By Design</span>
                  <p className="text-base text-amber-900 font-medium mb-2">
                    This isn't a large impersonal program. It's a curated, intimate experience.
                  </p>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    With just 10 members, you'll receive personalized attention, build authentic relationships, and experience the kind of deep healing that only happens in safe, connected spaces.<br />
                    Every person gets seen, heard, and supported.
                  </p>
                </div>
              </div>

              {/* Deadline Notice */}
              <div className="mt-4 p-4 bg-rose-50 border border-rose-200 rounded-lg">
                <p className="text-center text-sm text-gray-700">
                  <span className="font-semibold text-rose-700">{"$100 discount available until Feb 20, 2026"}</span>
                  <br />
                  <span className="text-gray-600">{"Path One begins March 5, 2026"}</span>
                </p>
              </div>

              {/* What's Included Reminder */}
              <div className="mt-6 text-center text-sm text-gray-600">
                <p>✓ live group coaching</p>
                <p>✓ Lifetime access to journaling workbooks</p>
                <p>✓ Community accountability & encouragement</p>
              </div>


              {/* Gentle Closing Message */}
              <div className="mt-12 mb-6 max-w-2xl mx-auto">
                <div className="bg-gradient-to-br from-warm-50 to-sage-50 border-l-4 border-warm-500 rounded-lg p-8 shadow-md">
                  <p className="text-lg md:text-xl leading-relaxed text-center mb-3">
                    <span className="font-bold text-sage-800">You've read about the program.</span>
                  </p>
                  <p className="text-lg md:text-xl leading-relaxed text-center mb-6">
                    <span className="font-bold text-sage-800">You've imagined what's possible.</span>
                  </p>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed text-center">
                    When you're ready, take the next step that feels right for you. Maybe that means giving yourself more space, slowing down, and noticing what you're wanting right now.
                  </p>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed text-center mt-4">{"You might feel ready to sign up for a group, or simply reach out to a trusted friend or family member. Whatever you choose, listen closely, be kind to yourself, and move at a pace that feels good."}</p>
                  <p className="text-lg md:text-xl leading-relaxed text-center mt-6">
                    <span className="font-bold text-sage-800">{"And remember: your process is excellent!"}</span>
                  </p>
                </div>
              </div>

              {/* Sign Me Up Button */}
              <div className="mt-8 text-center">
                <Link to="/path-one-enrollment" className="inline-block px-10 py-5 bg-warm-600 text-white rounded-full text-xl font-bold hover:bg-warm-700 transition-all duration-300 hover:shadow-2xl hover:scale-105 transform" data-tracking-id="link-80530b35e96caeca"><span>
                  Sign Me Up!
                </span></Link>
                <p className="text-xs text-gray-500 mt-3">You'll be redirected to enrollment page</p>
              </div>
            </div>

            {/* Who This Is For */}
            <div className="bg-gradient-to-br from-sage-50 to-warm-50 p-8 md:p-10 rounded-2xl border border-sage-200 shadow-md mb-12">
              <h3 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-6">Who This Is For</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-warm-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-lg">{"You've spent years trying to \"think\" your way out of people-pleasing and you're ready to heal through the body"}</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-warm-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-lg">You're ready to feel safe in your body, clear in your boundaries, and more alive in your relationships</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-warm-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-lg">You sometimes still say yes when your body is saying no</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-warm-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-lg">{"You're ready to slow down, breathe, and rest, without guilt"}</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-warm-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-lg">You want to learn in community with other women who understand</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-warm-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-lg">{"You're ready to do experiential work, not just talk about change"}</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-warm-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-lg">You value a trauma-informed approach that honors your nervous system</span>
                </li>
              </ul>
            </div>
            
            {/* Connection Image - Moved after Who This Is For */}
            <div className="mb-12 -mx-0 relative">
              <img alt="Women connecting and embracing, representing authentic relationships and community support" className="w-full h-[480px] object-cover rounded-2xl shadow-lg" loading="lazy" src="https://maker-devi-dev.s3-accelerate.amazonaws.com/uploads/user_33tbl7B6e1bXN6KW4wjrevvqrth/cmhd1gtfc0001vb0yeeofdn9z/images/unsplash-image-nisb9outpfa-7uivfoghw6l.jpg" />
              <span className="group/attribution absolute bottom-4 right-4 text-[10px] text-white outline outline-1 outline-white px-[6px] rounded-lg z-10 min-w-4 max-w-[calc(100%-16px)] h-4 leading-4 truncate text-center shadow">
                <span className="block drop-shadow-md group-hover/attribution:hidden">i</span>
                <span className="hidden drop-shadow-md group-hover/attribution:block">
                  Image on{' '}
                  <a href="https://unsplash.com?utm_source=Maker%20AI&utm_medium=referral" target="_blank" rel="noopener noreferrer" style={{
                  color: '#fff',
                  textDecoration: 'none'
                }} data-tracking-id="link-07d19dd88b44db6e">
                    Unsplash
                  </a>{' '}
                  by{' '}
                  <a href="https://unsplash.com/@makeupartist60?utm_source=makerdevi&utm_medium=referral" target="_blank" rel="noopener noreferrer" style={{
                  color: '#fff',
                  textDecoration: 'none'
                }} data-tracking-id="link-a750788eb067fb79">
                    Susan Kirsch
                  </a>
                </span>
              </span>
            </div>

            {/* Trauma-Informed Approach */}
            <div className="bg-gradient-to-br from-warm-50 to-sage-50 p-8 md:p-10 rounded-2xl border border-warm-200 shadow-md mb-12">
              <h3 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-6">A Trauma-Informed, Somatic Approach</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">{"Rooted in neuroscience, attachment theory, somatic therapy, and Internal Family Systems, the Group Coaching Program honors your body's innate intelligence. You won't be asked to push, perform, or force change. You'll be guided to listen, notice, and gently expand your capacity for aliveness and connection."}</p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                This is not self-help through willpower.
              </p>
              <p className="text-xl font-semibold text-warm-700">It's liberation and self-reclamation through safety.</p>
            </div>

            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mt-16 mb-6">What You'll Experience</h2>
            <div className="space-y-6 mb-12">
              <div className="bg-sage-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-sage-800 mb-3">Weekly Experiential Practices</h3>
                <p className="text-gray-600">{"Each session is experiential with hands-on practice time with a partner to help you embody what you're learning. These practices are not intellectual exercises, they are practices to experience your body's wisdom and capacity."}</p>
              </div>

              <div className="bg-sage-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-sage-800 mb-3">Nervous System Regulation</h3>
                <p className="text-gray-600">{"Learn practical tools to regulate your nervous system so you feel safe enough to say no. When your body feels safe, authentic kind asks and boundaries become natural."}</p>
              </div>

              <div className="bg-sage-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-sage-800 mb-3">Sharing in Community</h3>
                <p className="text-gray-600 mb-3">
                  This is a closed, confidential group where you can be yourself. Share your struggles, celebrate your wins, and feel truly seen by women who understand what you're going through.
                </p>
              </div>

              <div className="bg-sage-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-sage-800 mb-3">Lifetime Resources</h3>
                <p className="text-gray-600">{"Each module includes handouts you'll keep and comeback to as you come home to yourself, anytime you need grounding or a gentle reminder."}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-cream-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-12 text-center">What Group Members Are Saying</h2>
          
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <p className="text-lg text-gray-700 mb-4 italic">"For the first time, I'm learning to listen to my body instead of pushing through. The LMB mediation/practices have given me tools I use every day naturally- my body starts shifting into expansion and grounding while doing daily mundane chores like cooking and organizing "</p>
              <p className="font-semibold text-sage-800">— Maria L.</p>
              <p className="text-sm text-gray-500 italic">Name changed to preserve confidentiality</p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <p className="text-lg text-gray-700 mb-4 italic">"I was in fight or flee most of the time before. I would set boundaries forcefully but now I can do it from a soft grounded place. The group and relationships formed in group became safe anchors for me. I know all of me is good. Including my parts that feel rage and have been called "too much" and "too intense".</p>
              <p className="font-semibold text-sage-800">— Jordan K.</p>
              <p className="text-sm text-gray-500 italic">Name changed to preserve confidentiality</p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="w-8 h-8 text-warm-600 flex-shrink-0 -ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <p className="text-lg text-gray-700 mb-4 italic">{"\"I'm choosing where I direct my attention.. and have started celebrating my small wins instead of focusing on what's was not working. I'm spending more time in a healing happy space vs getting stuck in my trauma spiral like I was most of my time\""}</p>
              <p className="font-semibold text-sage-800">— Neha P.</p>
              <p className="text-sm text-gray-500 italic">Name changed to preserve confidentiality</p>
            </div>
          </div>
        </div>
      </section>

      {/* Free Guide CTA */}
      <PDFLeadMagnet isOpen={isPDFModalOpen} onClose={() => setIsPDFModalOpen(false)} />
      
      {/* Healing Callout Box - Right Before Final CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="glass-warm border-l-4 border-warm-400 p-8 md:p-10 rounded-lg shadow-lg">
            <p className="text-2xl md:text-3xl font-light text-sage-800 leading-relaxed text-center">{"Healing doesn't happen by forcing change. It happens when our body feels safe enough to try something new."}</p>
          </div>
        </div>
      </section>
      
      {/* Final Sign Me Up CTA - Location 3 for "14 spots only" */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-br from-warm-50 to-sage-50">
        <div className="max-w-3xl mx-auto text-center">
          {/* Limited Spots Badge - Exclusive Final CTA */}
          <div className="mb-6">
            <div className="inline-block px-8 py-4 bg-gradient-to-r from-amber-500 via-warm-500 to-rose-500 text-white rounded-full mb-3 animate-pulse shadow-2xl">
              <span className="text-xl font-bold">Your Intimate Healing Circle Awaits - 10 members</span>
            </div>
            <p className="text-sm text-gray-700 max-w-xl mx-auto">
              Limited enrollment ensures every participant receives the personalized support and authentic connection needed for lasting transformation.
            </p>
          </div>
          
          <div className="inline-block px-4 py-2 bg-warm-100 rounded-full mb-6">{"$100 discount available until February 20, 2026."}</div>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-4">
            Ready to Begin Your Healing Journey?
          </h2>
          <p className="text-lg text-gray-600 mb-8">{"Join Path One starting on March 5, 2026. Schedule your interview to see if we are a good match. "}</p>
          <Link to="/path-one-enrollment" className="inline-block px-12 py-6 bg-warm-600 text-white rounded-full text-xl font-bold hover:bg-warm-700 transition-all duration-300 hover:shadow-2xl hover:scale-105 transform mb-4" data-tracking-id="link-4327afd0963c63e3"><span>Sign me up!</span></Link>
          <p className="text-sm text-gray-500">
            Questions? <a href="mailto:your-email@example.com" className="text-warm-600 hover:text-warm-700 underline" data-tracking-id="link-bc0fe8ce805e96d3">Get in touch</a>
          </p>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-4">Wanting a free resource at this time?</h2>
          <p className="text-lg text-gray-600 mb-8">Download my free guide called "People-pleasing in the Therapy Room" to understand power dynamics in your relationship with your therapist.</p>
          <button onClick={() => setIsPDFModalOpen(true)} className="inline-block px-8 py-4 bg-sage-600 text-white rounded-full text-lg font-semibold hover:bg-sage-700 transition-all duration-300 hover:shadow-xl hover:scale-105 transform" data-tracking-id="btn-55392dee2454aea9">
            Get the Free Guide
          </button>
        </div>
      </section>

      {/* Individual Sessions CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-sage-50">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-warm-100 rounded-full mb-6">
            <span className="text-warm-700 text-sm font-semibold">Individual Sessions Available</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-4">Wanting individual sessions covering<br />the same material?</h2>
          <p className="text-lg text-gray-600 mb-8">Work one-on-one through the Group Coaching Program at your own pace with personalized attention and support.</p>
          <a className="inline-block px-8 py-4 bg-warm-600 text-white rounded-full text-lg font-semibold hover:bg-warm-700 transition-all duration-300 hover:shadow-xl hover:scale-105 transform" href="https://calendly.com/yphilipos/30min" data-tracking-id="link-7a39e0c50e5aab34">
            Schedule a Consult
          </a>
        </div>
      </section>

      <Footer />
    </div>;
}
export default GroupCoachingService;